import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import clsx from 'clsx';
import {
  Alert,
  AlertBody,
  AlertIcon,
} from '@lifeomic/chroma-react/components/Alert';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { XCircle } from '@lifeomic/chromicons';
import { useTemplatedSurveyResponse } from '../../util/useTemplatedSurveyResponse';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import TemplateResult from './TemplateResult';

const messages = defineMessages({
  errorHeader: {
    id: 'LoadableTemplateResult.errorHeader',
    defaultMessage: 'Error Generating Template',
  },

  errorContent: {
    id: 'LoadableTemplateResult.errorContent',
    defaultMessage:
      'An unexpected error occurred generating the template content',
  },
});

export const useStyles = makeStyles((_theme: Theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
  },
}));

export type LoadableTemplateResultOwnProps = {
  className?: string;
  templateId: string;
  responseId: string;
  surveyId: string;
};

export type LoadableTemplateResultProps = LoadableTemplateResultOwnProps;

// @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
const LoadableTemplateResult: React.FC<LoadableTemplateResultProps> = ({
  templateId,
  responseId,
  surveyId,
  className,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const { isLoading, response } = useTemplatedSurveyResponse({
    templateId,
    responseId,
    surveyId,
  });

  if (isLoading) {
    return (
      <div className={clsx(classes.root, className)}>
        <DotLoader size={0} />
      </div>
    );
  } else if (response.error) {
    <div className={clsx(classes.root, className)}>
      <Alert statusType="error">
        <AlertIcon icon={XCircle} />
        <AlertBody>
          <Text size="subbody" weight="bold">
            {formatMessage(messages.errorHeader)}
          </Text>
          <Text size="caption">{formatMessage(messages.errorContent)}</Text>
        </AlertBody>
      </Alert>
    </div>;
  } else {
    return (
      <TemplateResult className={className} templateResult={response.result} />
    );
  }
};

export default React.memo(LoadableTemplateResult);
